// The look of a round in flight, as DATA (ui-map-interface.md §6.3, R164).
// Mockup round eight picked **Terminal Burn** as the default; Two-Stage,
// Spindle, Ribbon and Bubble Wake ship as the `torpedo` field of the World
// body of a look (`look.world.torpedo`, R807).
//
// A style is data, not a layer. It answers one question - which sprites
// draw this round, this frame - and the torpedo layer owns the pool, the
// textures and the camera jolt. A theme pack therefore adds a style by
// adding one entry to `TORPEDO_STYLE_DEFS` and one name to
// `TORPEDO_STYLES`, and it touches neither an entry nor a layer.
//
// Everything here is a pure function of the flight state the layer hands
// in, so the geometry is unit-testable without a canvas.
//
// This module imports NO renderer (R431). The client bakes its atlas from
// `paint` and `SHAPE` directly, so the shapes are stated one time and every
// atlas bakes the same art.
import type { TorpedoStyle } from '../ui/settings';
import { TAU, clamp01, mixInk } from './recipe-kit';
// The palette and the heavy-round scale ride presentation.toml
// [presentation.torpedo] (R312).
import {
  TORPEDO_AMBER, TORPEDO_FOAM, TORPEDO_HEAVY_SCALE, TORPEDO_HOT,
  TORPEDO_PLUME, TORPEDO_STEEL, TORPEDO_WHITE,
} from '../gen/presentation';

/** The white shapes every style tints. One texture, many rounds (§10.1). */
export type TexKey = 'dart' | 'glow' | 'streak' | 'bubble' | 'hull' | 'bar';

/** One sprite of one round, in the round's own frame unless `abs` is set. */
export interface Piece {
  tex: TexKey;
  /** Offset from the round: ox along the heading, oy across it. World px
   *  when `abs` is set. */
  ox: number;
  oy: number;
  /** Rotation on top of the heading, in radians. Absolute when `abs`. */
  rot: number;
  /** Full size in map px: `len` along the piece's own x, `wid` across it. */
  len: number;
  wid: number;
  tint: number;
  alpha: number;
  /** `ox`/`oy` are world px and `rot` is an absolute heading. */
  abs: boolean;
}

/**
 * The pieces of one round, reused frame to frame (§10.7): a style writes
 * its sprites through `put` and allocates nothing after the first frame.
 * The layer reads `n` records from `list` and then calls `clear`.
 */
export class Pieces {
  readonly list: Piece[] = [];
  n = 0;

  put(
    tex: TexKey, ox: number, oy: number, rot: number, len: number, wid: number,
    tint: number, alpha: number, abs = false,
  ): void {
    let p = this.list[this.n];
    if (!p) {
      p = { tex, ox, oy, rot, len, wid, tint, alpha, abs };
      this.list.push(p);
    } else {
      p.tex = tex; p.ox = ox; p.oy = oy; p.rot = rot;
      p.len = len; p.wid = wid; p.tint = tint; p.alpha = alpha; p.abs = abs;
    }
    this.n++;
  }

  clear(): void { this.n = 0; }
}

/** A mark the layer shed behind a round: a bubble, or a spent first stage. */
export interface Mark {
  x: number;
  y: number;
  /** The round's travel figure when the mark was shed. */
  travel: number;
}

/** What a style is given. The client simulates nothing: `u` comes off the
 *  wire (`TorpedoState.run_pct`) and `travel` is the distance the round has
 *  been *seen* to move, which is what a phase and a shed rate need. */
export interface RoundView {
  x: number;
  y: number;
  heading: number;
  /** 0..1 along the run, from the wire. */
  u: number;
  /** 0.125..1, the look-only speed ramp — see `runSpeed`. */
  spd: number;
  /** px of travel since the client first saw the round. */
  travel: number;
  /** Terminal Burn's heat ramp, 0..1: `u` in flight, falling to 0 (the
   *  firer's ink) while a Stonefish waits (R2848). */
  heat: number;
  /** The share of the shed marks left, 0..1: 1 in flight, falling to 0
   *  as a waiting round's bubbles pop in place (R2848). */
  wake: number;
  heavy: boolean;
  color: number;
  /** Shed marks, oldest first. Empty unless the style asks for them. */
  marks: Mark[];
  /** Where the round crossed `stageAt`, and how long ago in seconds. */
  stage: { x: number; y: number; age: number } | null;
}

export interface TorpedoStyleDef {
  /** px of travel between two shed marks; 0 or absent sheds none. */
  readonly shedPx?: number;
  /** How many marks the layer keeps. */
  readonly marks?: number;
  /** The run fraction whose crossing the layer must remember. */
  readonly stageAt?: number;
  /** The style jolts the camera for two frames on a hit. It goes through
   *  `camera.setShake`, so `settings.screenshake` turns it off. */
  readonly jolt?: boolean;
  /** Appends this frame's sprites. Pure — no state of its own. */
  pieces(r: RoundView, out: Pieces): void;
}

const WHITE = TORPEDO_WHITE;
const AMBER = TORPEDO_AMBER;
const HOT = TORPEDO_HOT;
const PLUME = TORPEDO_PLUME;
const FOAM = TORPEDO_FOAM;
const STEEL = TORPEDO_STEEL;

/** A heavy round is the same shape, 45% larger — the kind must still read. */
const HEAVY_SCALE = TORPEDO_HEAVY_SCALE;

/**
 * The look-only speed ramp of mockup round eight: distance covered is
 * `0.30u + 0.70u³`, so speed is `(0.30 + 2.10u²) / 2.40`. The sim moves
 * every round at a constant 340 px/s and this changes none of that — the
 * stretch, the trail and the heat sell the acceleration, the ground speed
 * stays flat.
 */
export function runSpeed(u: number): number {
  const c = clamp01(u);
  return (0.30 + 2.10 * c * c) / 2.40;
}

/** Terminal Burn's heat ramp: faction out of the tube, amber at half range,
 *  white-red in the last quarter. */
export function heatColor(faction: number, u: number): number {
  return u < 0.55
    ? mixInk(faction, AMBER, u / 0.55)
    : mixInk(AMBER, HOT, (u - 0.55) / 0.45);
}

/* ================= the five styles ================= */

/** 07 Terminal Burn — the default. Colour and motion: the round heats and
 *  pulses faster the nearer it gets, so how close a round is to something
 *  of yours reads from across the map. */
const terminalBurn: TorpedoStyleDef = {
  jolt: true,
  pieces(r, out) {
    const s = r.heavy ? HEAVY_SCALE : 1;
    const col = heatColor(r.color, r.heat);
    const pulse = 0.55 + 0.45 * Math.sin(r.travel * (0.30 + 0.85 * r.heat));
    const len = (6 + 20 * r.spd) * s;
    out.put('streak', -len / 2, 0, 0, len, 3.0 * s, col, 0.22 + 0.30 * r.spd);
    const halo = 2 * (2.4 + 2.6 * r.heat * pulse) * s;
    out.put('glow', 0, 0, 0, halo, halo, col, 0.16 + 0.30 * pulse * r.heat);
    out.put('dart', 0.7 * s, 0, 0, (5.3 + 2.4 * r.spd) * s, 3.2 * s, col, 0.95);
    const core = 2 * (0.95 + 0.55 * pulse) * s;
    out.put('glow', 0.8 * s, 0, 0, core, core, WHITE, 0.92);
  },
};

/** 10 Two-Stage — motion. A booster lights at 42% of the run: an amber
 *  flare, a first stage left hanging in the water, and a plume behind a
 *  round that steps up in speed. The acceleration becomes an event. */
const TWO_STAGE_AT = 0.42;
const twoStage: TorpedoStyleDef = {
  stageAt: TWO_STAGE_AT,
  jolt: true,
  pieces(r, out) {
    const s = r.heavy ? HEAVY_SCALE : 1;
    const boosted = r.u >= TWO_STAGE_AT;
    if (r.stage) {
      const { x, y, age } = r.stage;
      if (age < 0.9) {
        const d = 2 * (1.5 + 7 * age) * s;
        out.put('bubble', x, y, 0, d, d, FOAM, 0.40 * (1 - age / 0.9), true);
      }
      if (age < 0.20) {
        const d = 2 * (1.6 + 6 * (age / 0.20)) * s;
        out.put('glow', x, y, 0, d, d, PLUME, 0.80 * (1 - age / 0.20), true);
      }
      if (age < 1.4) {
        out.put(
          'dart', x, y, r.heading, 3.4 * s, 2.2 * s, r.color,
          0.30 * (1 - age / 1.4), true,
        );
      }
    }
    const plume = (boosted ? 4 + 22 * r.spd : 2 + 3 * r.spd) * s;
    out.put(
      'streak', -3 * s - plume / 2, 0, 0, plume, (boosted ? 3.4 : 2.0) * s,
      boosted ? PLUME : r.color, boosted ? 0.62 : 0.26,
    );
    const len = (boosted ? 6 + 11 * r.spd : 5.5) * s;
    out.put('dart', 0.4 * s, 0, 0, len, 3.0 * s, r.color, 0.92);
    out.put('glow', len * 0.28, 0, 0, 1.9 * s, 1.9 * s, WHITE, 0.95);
  },
};

/** 09 Spindle — motion alone. A slim hull with a tail bar that spins up as
 *  the run closes: a blip never spins. */
const spindle: TorpedoStyleDef = {
  pieces(r, out) {
    const s = r.heavy ? HEAVY_SCALE : 1;
    const tail = -3.6 * s;
    const back = (5 + 16 * r.spd) * s;
    out.put(
      'streak', tail - back / 2, 0, 0, back, 2.2 * s, r.color,
      0.08 + 0.18 * r.spd,
    );
    out.put('hull', -0.4 * s, 0, 0, 6.8 * s, 2.3 * s, r.color, 0.85);
    const rr = (1.7 + 1.2 * r.spd) * s;
    if (r.spd > 0.45) {
      out.put('glow', tail, 0, 0, 2 * rr, 2 * rr, STEEL, 0.14 * r.spd);
    }
    const phase = r.travel * 0.85;
    for (let i = 0; i < 3; i++) {
      const a = phase - i * 0.44;
      const alpha = (0.6 - 0.17 * i) * (0.30 + 0.70 * r.spd);
      out.put('bar', tail, 0, a, 2 * rr, 0.75, STEEL, alpha);
      out.put(
        'bar', tail, 0, a + Math.PI / 2, 1.6 * rr, 0.75, STEEL, alpha * 0.7,
      );
    }
    out.put('dart', 2.6 * s, 0, 0, 3.4 * s, 2.2 * s, WHITE, 0.95);
  },
};

/** 06 Ribbon — shape. One tapered ribbon whose length is pinned to the
 *  speed ramp: a 5-px stub at launch, a 60-px streak at impact. The
 *  cheapest thing in the set, and it deletes the old per-segment trail. */
const ribbon: TorpedoStyleDef = {
  pieces(r, out) {
    const s = r.heavy ? HEAVY_SCALE : 1;
    const len = (5 + 55 * r.spd) * s;
    out.put(
      'streak', -len / 2, 0, 0, len, 3.8 * s, r.color, 0.20 + 0.68 * r.spd,
    );
    // The nose glow stands in for the mockup's shadow blur. It stays well
    // under the sonar blip's 12-px width, because a soft faction disc of
    // blip size is the exact collision this round was called to end.
    const halo = (2.6 + 3.0 * r.spd) * s;
    out.put('glow', 0, 0, 0, halo, halo, r.color, 0.15 + 0.18 * r.spd);
    out.put('dart', 0.1 * s, 0, 0, 5.0 * s, 3.4 * s, r.color, 0.95);
    out.put('glow', 0.4 * s, 0, 0, 2.3 * s, 2.3 * s, WHITE, 0.95);
  },
};

/** 05 Bubble Wake — shape and texture. A hard dart sheds cavitation
 *  bubbles that swell and collapse behind it, packed tighter the faster it
 *  runs. The wake becomes a countable object instead of a smear. When the
 *  round stops, `wake` pops the bubbles in place: each shrinks to 0.4 of
 *  its size as it fades (round 133). */
const bubbleWake: TorpedoStyleDef = {
  shedPx: 3.4,
  marks: 15,
  pieces(r, out) {
    const s = r.heavy ? HEAVY_SCALE : 1;
    const pop = r.wake;
    if (pop > 0) for (const m of r.marks) {
      const d = r.travel - m.travel;
      if (d > 34) continue;
      const a = d / 34;
      const rr = a < 0.45 ? 0.5 + 2.1 * (a / 0.45) : 2.6 * (1 - (a - 0.45) / 0.55);
      const dia = 2 * Math.max(0.2, rr) * s * (0.4 + 0.6 * pop);
      out.put('bubble', m.x, m.y, 0, dia, dia, FOAM, 0.48 * (1 - a) * pop, true);
    }
    out.put('dart', 0.2 * s, 0, 0, 6.0 * s, 2.3 * s, r.color, 0.92);
    out.put('glow', 1.5 * s, 0, 0, 1.7 * s, 1.7 * s, WHITE, 0.95);
  },
};

/** Every style the client can draw. A theme pack adds one entry here and
 *  one name in `settings.ts`, and nothing else changes. */
export const TORPEDO_STYLE_DEFS: Record<TorpedoStyle, TorpedoStyleDef> = {
  'terminal-burn': terminalBurn,
  'two-stage': twoStage,
  spindle,
  ribbon,
  'bubble-wake': bubbleWake,
};

/* ================= the white texture set ================= */

/** The nominal shape size of each texture in map px at scale 1. A piece's
 *  `len`/`wid` set the sprite's width and height, so the figures here only
 *  fix the aspect the artwork is drawn at. */
export const SHAPE: Record<TexKey, [number, number]> = {
  dart: [16, 8],
  glow: [16, 16],
  streak: [32, 8],
  bubble: [16, 16],
  hull: [16, 8],
  bar: [16, 4],
};

/** Paints one white shape into a 2D context that is already scaled.
 *  Exported because the fast client's atlas bakes the same shapes into
 *  one texture rather than into a texture each. */
export function paint(key: TexKey, ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  switch (key) {
    case 'dart': {
      // A tapered dart, nose to the right, notched at the tail.
      ctx.beginPath();
      ctx.moveTo(w, h / 2);
      ctx.lineTo(0, 0);
      ctx.lineTo(w * 0.22, h / 2);
      ctx.lineTo(0, h);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'glow': {
      const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.45, 'rgba(255,255,255,0.55)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      break;
    }
    case 'streak': {
      // The head sits at the right edge, full width and opaque; the tail
      // thins and fades out to the left. The alpha ramp is baked in, so a
      // trail costs one sprite and no per-frame Graphics.
      const g = ctx.createLinearGradient(w, 0, 0, 0);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(255,255,255,0.45)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(w, 0);
      ctx.lineTo(0, h * 0.44);
      ctx.lineTo(0, h * 0.56);
      ctx.lineTo(w, h);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'bubble': {
      ctx.lineWidth = h * 0.10;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, w / 2 - h * 0.06, 0, TAU);
      ctx.stroke();
      break;
    }
    case 'hull': {
      ctx.beginPath();
      ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, TAU);
      ctx.fill();
      break;
    }
    case 'bar': {
      ctx.lineCap = 'round';
      ctx.lineWidth = h * 0.6;
      ctx.beginPath();
      ctx.moveTo(h * 0.3, h / 2);
      ctx.lineTo(w - h * 0.3, h / 2);
      ctx.stroke();
      break;
    }
  }
}
